import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Slider } from '@/components/ui/slider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { ChartConfig } from '@/components/ui/chart'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Line, LineChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from 'recharts'
import { request } from '@/lib/request'
import { toast } from 'sonner'

interface TaskTrendChartProps {
  taskName: string
  refreshFlag?: boolean
  showTimeSelector?: boolean
  className?: string
}

interface ChartDataItem {
  date: string
  balance: number
}

const chartConfig = {
  balance: {
    label: '余额',
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig

export default function TaskTrendChart({
  taskName,
  refreshFlag,
  showTimeSelector = true,
  className = ''
}: TaskTrendChartProps) {
  const [chartData, setChartData] = useState<ChartDataItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hours, setHours] = useState([3])
  const [presetTime, setPresetTime] = useState('3h')

  const fetchTaskTrend = async () => {
    if (!taskName) return

    setLoading(true)
    try {
      const resp = await request('/common/apis/v1/task_trend', {
        task_name: taskName,
        h: `${hours[0]}h`
      })

      if (resp && resp.code === 0) {
        const hits = resp.data?.hits?.hits || []

        // 按日期排序
        const sortedData = hits
          .map((item: any) => ({
            date: item._source?.date || item.date || '',
            balance: parseFloat(item._source?.balance || item.balance || 0)
          }))
          .filter((item: ChartDataItem) => item.date) // 过滤掉无效数据
          .sort((a: ChartDataItem, b: ChartDataItem) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
          )

        setChartData(sortedData)
      } else {
        toast.error('获取趋势数据失败')
      }
    } catch (error) {
      console.error('Failed to fetch task trend:', error)
      toast.error('获取趋势数据失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (taskName) {
      fetchTaskTrend()
    }
  }, [hours, taskName])

  useEffect(() => {
    if (refreshFlag && taskName) {
      fetchTaskTrend()
    }
  }, [refreshFlag])

  // 格式化日期显示
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr)
      const month = date.getMonth() + 1
      const day = date.getDate()
      const hour = date.getHours()
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${month}/${day} ${hour}:${minute}`
    } catch {
      return dateStr
    }
  }

  // 预设时间快捷选择
  const handlePresetChange = (value: string) => {
    setPresetTime(value)
    const hourValue = parseInt(value.replace('h', ''))
    setHours([hourValue])
  }

  const presetOptions = [
    { value: '1h', label: '1小时' },
    { value: '3h', label: '3小时' },
    { value: '6h', label: '6小时' },
    { value: '12h', label: '12小时' },
    { value: '24h', label: '24小时' },
    { value: '48h', label: '48小时' },
    { value: '72h', label: '72小时' },
  ]

  return (
    <Card className={`w-full border-border/40 ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">任务趋势</CardTitle>
            <div className="text-sm text-muted-foreground mt-1">
              {taskName} - {hours[0]} 小时数据
            </div>
          </div>
          {showTimeSelector && (
            <Select value={presetTime} onValueChange={handlePresetChange}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {presetOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            加载中...
          </div>
        ) : chartData.length > 0 ? (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  className="text-xs"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  className="text-xs"
                  tickFormatter={(value) => `¥${value}`}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(label) => formatDate(label as string)}
                      formatter={(value) => [`¥${Number(value).toFixed(2)}`, '余额']}
                    />
                  }
                />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="var(--color-balance)"
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-balance)', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            暂无数据
          </div>
        )}

        {/* 时间范围滑块 */}
        {showTimeSelector && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">自定义时间范围</span>
              <span className="font-medium">{hours[0]} 小时</span>
            </div>
            <Slider
              value={hours}
              onValueChange={setHours}
              min={1}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1h</span>
              <span>100h</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
